import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase/config';
import { Building2, Check, X, Clock, UserCheck, Mail, AlertCircle } from 'lucide-react';

function ReceivedInvites({ user }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    if (user?.uid) {
      loadInvites();
      loadCurrentCompany();
    }
    // eslint-disable-next-line
  }, [user]);

  const loadInvites = async () => {
    try {
      const q = query(
        collection(db, 'staffInvites'),
        where('email', '==', user.email?.toLowerCase()),
        where('status', '==', 'pending')
      );
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
      setInvites(list);
    } catch (e) {
      console.error('초대 로딩 실패:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentCompany = async () => {
    if (user?.companyId) {
      try {
        const companyDoc = await getDoc(doc(db, 'users', user.companyId));
        if (companyDoc.exists()) {
          setCurrentCompany({ id: companyDoc.id, ...companyDoc.data() });
        }
      } catch (e) {
        console.error('소속 업체 로딩 실패:', e);
      }
    }
  };

  const handleAccept = async (invite) => {
    if (user?.companyId) {
      alert('이미 다른 업체에 소속되어 있습니다.');
      return;
    }
    if (!window.confirm(invite.companyName + '에 직원으로 가입하시겠습니까?')) return;

    setProcessing(invite.id);
    try {
      // 2026-08-05 보안수정: 초대 수락은 CF에서 초대 검증 후 원자 처리 (클라 직접 write 폐쇄)
      const acceptFn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'acceptStaffInvite');
      await acceptFn({ inviteId: invite.id });

      alert('업체 가입이 완료되었습니다.');
      window.location.reload();
    } catch (e) {
      console.error('수락 실패:', e);
      alert(e?.message || '오류가 발생했습니다.');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (invite) => {
    if (!window.confirm('초대를 거절하시겠습니까?')) return;

    setProcessing(invite.id);
    try {
      await updateDoc(doc(db, 'staffInvites', invite.id), {
        status: 'rejected',
        rejectedAt: serverTimestamp()
      });
      setInvites(prev => prev.filter(i => i.id !== invite.id));
    } catch (e) {
      console.error('거절 실패:', e);
      alert('오류가 발생했습니다.');
    } finally {
      setProcessing(null);
    }
  };

  const handleLeave = async () => {
    if (!currentCompany) return;
    if (!window.confirm((currentCompany.businessName || currentCompany.name) + '에서 탈퇴하시겠습니까?')) return;

    // 2026-10-12 A-7: 종전 본인 users·업체 users(staff 배열) 직접 쓰기는 rules에 막혀 실패했다 →
    //   서버 callable leaveCompany(입력 없음 — 호출자 본인, 회사 직원 목록 정리는 서버가 함)로 교체.
    try {
      const leave = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'leaveCompany');
      await leave({});
      alert('탈퇴 완료');
      window.location.reload();
    } catch (e) {
      // 조용한 실패 금지 — 서버가 준 사유를 그대로 보여 준다
      console.error('탈퇴 실패:', e?.code || '', e?.message || e);
      const serverMsg = (e?.code && e.code !== 'functions/internal' && e?.message) ? e.message : '';
      alert(serverMsg || '탈퇴에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.getFullYear() + '.' + (d.getMonth() + 1) + '.' + d.getDate();
  };

  const getTypeLabel = (type) => {
    if (type === 'clinic' || type === 'dental') return '치과';
    if (type === 'lab') return '기공소';
    return '업체';
  };

  if (loading) return <div style={styles.loading}>로딩 중...</div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.iconBox}><Mail size={24} color="#ffffff" /></div>
        <div>
          <h1 style={styles.title}>받은 초대</h1>
          <p style={styles.subtitle}>업체에서 보낸 직원 초대를 확인하세요</p>
        </div>
      </div>

      {currentCompany && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}><UserCheck size={18} /> 현재 소속</h2>
          <div style={styles.currentCard}>
            <div style={styles.avatar}><Building2 size={24} color="#6366f1" /></div>
            <div style={styles.info}>
              <div style={styles.name}>{currentCompany.businessName || currentCompany.name}</div>
              <div style={styles.type}>{getTypeLabel(currentCompany.businessType)}</div>
            </div>
            <button onClick={handleLeave} style={styles.leaveBtn}>탈퇴</button>
          </div>
        </div>
      )}

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}><Clock size={18} /> 대기 중인 초대</h2>
        
        {invites.length === 0 ? (
          <div style={styles.empty}>
            <AlertCircle size={40} color="#cbd5e1" />
            <p style={styles.emptyText}>받은 초대가 없습니다</p>
          </div>
        ) : (
          <div style={styles.list}>
            {invites.map(invite => (
              <div key={invite.id} style={styles.card}>
                <div style={styles.cardHeader}>
                  <div style={styles.companyAvatar}><Building2 size={20} color="#fff" /></div>
                  <div style={styles.cardInfo}>
                    <div style={styles.companyName}>{invite.companyName}</div>
                    <div style={styles.companyType}>{getTypeLabel(invite.companyBusinessType)}</div>
                  </div>
                  <div style={styles.date}>{formatDate(invite.createdAt)}</div>
                </div>
                
                {invite.message && <div style={styles.message}>"{invite.message}"</div>}

                <div style={styles.actions}>
                  <button 
                    onClick={() => handleAccept(invite)} 
                    disabled={processing === invite.id || user?.companyId}
                    style={{...styles.acceptBtn, ...(user?.companyId ? styles.disabled : {})}}
                  >
                    <Check size={16} /> 수락
                  </button>
                  <button 
                    onClick={() => handleReject(invite)} 
                    disabled={processing === invite.id}
                    style={styles.rejectBtn}
                  >
                    <X size={16} /> 거절
                  </button>
                </div>

                {user?.companyId && <div style={styles.warning}>* 이미 소속된 업체가 있습니다</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { padding: 24, maxWidth: 800, margin: '0 auto' },
  loading: { textAlign: 'center', padding: 60, color: '#64748b' },
  header: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 },
  iconBox: { width: 56, height: 56, borderRadius: 14, backgroundColor: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  title: { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  subtitle: { fontSize: 14, color: '#64748b', margin: '4px 0 0' },
  section: { marginBottom: 28 },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: '#374151', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 },
  currentCard: { display: 'flex', alignItems: 'center', gap: 16, padding: 20, backgroundColor: '#eef2ff', borderRadius: 12, border: '1px solid #c7d2fe' },
  avatar: { width: 56, height: 56, borderRadius: 12, backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  info: { flex: 1 },
  name: { fontSize: 18, fontWeight: 700, color: '#0f172a' },
  type: { fontSize: 13, color: '#6366f1', fontWeight: 500, marginTop: 2 },
  leaveBtn: { padding: '10px 20px', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  empty: { textAlign: 'center', padding: 48, backgroundColor: '#f8fafc', borderRadius: 12, border: '1px dashed #e2e8f0' },
  emptyText: { fontSize: 16, fontWeight: 600, color: '#64748b', margin: '16px 0 0' },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: { padding: 20, backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e2e8f0' },
  cardHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 },
  companyAvatar: { width: 44, height: 44, borderRadius: 10, backgroundColor: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cardInfo: { flex: 1 },
  companyName: { fontSize: 16, fontWeight: 700, color: '#0f172a' },
  companyType: { fontSize: 13, color: '#6366f1', fontWeight: 500 },
  date: { fontSize: 13, color: '#94a3b8' },
  message: { padding: '12px 16px', backgroundColor: '#f8fafc', borderRadius: 8, fontSize: 14, color: '#475569', fontStyle: 'italic', marginBottom: 16 },
  actions: { display: 'flex', gap: 10 },
  acceptBtn: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 12, backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  rejectBtn: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 12, backgroundColor: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  disabled: { backgroundColor: '#e2e8f0', color: '#94a3b8', cursor: 'not-allowed' },
  warning: { marginTop: 12, fontSize: 13, color: '#dc2626' },
};

export default ReceivedInvites;
